import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { ICONS } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import TiersEditor from "./TiersEditor";

export const EMPTY_SERVICE = {
  category: "E-COMMERCE", price: 0, period: "once", price_min: null, price_max: null, auto_price_enabled: false,
  icon: "Sparkles", sort_order: 0, is_active: true, title_ru: "", title_en: "", title_cn: "",
  description_ru: "", description_en: "", description_cn: "", features_ru: [], features_en: [], features_cn: [], tiers: [],
};

const numOrNull = (v) => (v === "" || v === null ? null : Number(v));

function LangFields({ form, set, lang }) {
  return (
    <div className="space-y-3" data-testid={`lang-fields-${lang}`}>
      <div>
        <label>Название {lang.toUpperCase()}</label>
        <input value={form[`title_${lang}`]} onChange={(e) => set(`title_${lang}`, e.target.value)} className="mt-1" data-testid={`service-title-${lang}`} />
      </div>
      <div>
        <label>Описание {lang.toUpperCase()}</label>
        <textarea rows={2} value={form[`description_${lang}`]} onChange={(e) => set(`description_${lang}`, e.target.value)} className="mt-1" data-testid={`service-description-${lang}`} />
      </div>
      <div>
        <label>Преимущества {lang.toUpperCase()} (по одному в строке)</label>
        <textarea rows={3} value={form[`features_${lang}`].join("\n")}
          onChange={(e) => set(`features_${lang}`, e.target.value.split("\n"))} className="mt-1" data-testid={`service-features-${lang}`} />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label>{label}</label><div className="mt-1">{children}</div></div>;
}

export default function ServiceForm({ initial, categories, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY_SERVICE, ...initial });
  const [lang, setLang] = useState("ru");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const clean = (arr) => arr.map((s) => s.trim()).filter(Boolean);
    const payload = { ...form, features_ru: clean(form.features_ru), features_en: clean(form.features_en), features_cn: clean(form.features_cn) };
    try {
      if (initial.id) await api.put(`/admin/services/${initial.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Услуга сохранена");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0E0E16] border-[#D4AF37]/30 text-gray-100" data-testid="service-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{initial.id ? "✏️ Редактировать услугу" : "➕ Новая услуга"}</DialogTitle>
          <DialogDescription className="text-gray-400">Цены хранятся в рублях. Коридор цен нужен для автономии AI.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-6">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Категория">
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="service-category-select">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Цена ₽">
              <input type="number" min={0} required value={form.price} onChange={(e) => set("price", Number(e.target.value))} data-testid="service-price-input" />
            </Field>
            <Field label="Период">
              <select value={form.period} onChange={(e) => set("period", e.target.value)} data-testid="service-period-select">
                <option value="once">Разово</option>
                <option value="month">В месяц</option>
              </select>
            </Field>
            <Field label="Иконка">
              <select value={form.icon} onChange={(e) => set("icon", e.target.value)} data-testid="service-icon-select">
                {Object.keys(ICONS).map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Показывать на сайте">
              <select value={form.is_active ? "1" : "0"} onChange={(e) => set("is_active", e.target.value === "1")} data-testid="service-active-select">
                <option value="1">Да</option>
                <option value="0">Нет (скрыта)</option>
              </select>
            </Field>
          </div>

          <div className="glass rounded-2xl p-4 space-y-3">
            <div className="text-xs tracking-widest uppercase text-gold">Коридор цен для AI</div>
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <Field label="Мин ₽">
                <input type="number" min={0} value={form.price_min ?? ""} onChange={(e) => set("price_min", numOrNull(e.target.value))} data-testid="service-price-min" />
              </Field>
              <Field label="Макс ₽">
                <input type="number" min={0} value={form.price_max ?? ""} onChange={(e) => set("price_max", numOrNull(e.target.value))} data-testid="service-price-max" />
              </Field>
              <label className="flex items-center gap-2 !normal-case !tracking-normal text-sm text-gray-200 pb-3">
                <input type="checkbox" className="!w-4 !h-4" checked={form.auto_price_enabled} onChange={(e) => set("auto_price_enabled", e.target.checked)} data-testid="service-auto-price" />
                AI может менять цену
              </label>
            </div>
          </div>

          <div>
            <div className="flex gap-2 mb-3">
              {["ru", "en", "cn"].map((l) => (
                <button type="button" key={l} onClick={() => setLang(l)} className={`lang-btn ${lang === l ? "active" : ""}`} data-testid={`service-lang-tab-${l}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <LangFields form={form} set={set} lang={lang} />
          </div>

          <div>
            <div className="text-xs tracking-widest uppercase text-gold mb-3">Тарифы (tiers)</div>
            <TiersEditor tiers={form.tiers} onChange={(t) => set("tiers", t)} />
          </div>

          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-outline" data-testid="service-cancel-btn">Отмена</button>
            <button type="submit" disabled={saving} className="btn-gold" data-testid="service-save-btn">{saving ? "…" : "Сохранить"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
